// ============================================================================
// FLOW QUALITY FUNNEL VIEW
//
// Executive-grade funnel analytics page showing how referrals move through
// the ReferralGate pipeline from gate submission to closed-loop completion.
//
// Six stages in one strip:
//   1. Gate Submissions    — successful non-redirect committed submissions
//   2. Referrals Written   — unique referral core docs created in Firestore
//   3. Entered Ops         — first intake action (claim or touch)
//   4. Recovered from Not Yet — conditional: incomplete referrals later recovered
//   5. Triage Exceptions   — conditional: override / on-hold / priority escalation
//   6. Reached Closed Loop — terminal form state
//
// Stages 4 and 5 are subset / conditional populations — not every referral
// passes through them. They measure recovery and exception signals within
// the same funnel, not mandatory sequential steps.
//
// Data: reads from tenants/{tenantId}/rollups/daily/dates/{YYYY-MM-DD} only.
// Zero collection scans. Zero PHI reads.
//
// Access: tenant_admin, ops_manager, platform_admin (SECTOR_ANALYTICS)
// Route: 'flow-quality'
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { safeUserError } from '../../core/safeError.js';
import {
  Shield, FileText, ClipboardList, RefreshCw,
  AlertTriangle, CheckCircle2, ArrowRight,
  TrendingUp, Activity, ChevronDown, ChevronUp,
  RotateCcw, Info,
} from 'lucide-react';
import { AuthContext } from '../../auth/AuthContext.jsx';
import {
  getDailyRollups,
  buildDateRange,
  getFlowQualityTotals,
  getConversionMetrics,
} from '../../core/index.js';

// ============================================================================
// HELPERS
// ============================================================================

const pct = (num, den) => {
  if (!den || den === 0) return null;
  return Math.round((num / den) * 100);
};

const fmtPct = (val) => (val === null ? '—' : `${val}%`);

const fmtN = (n) => (typeof n === 'number' ? n.toLocaleString() : '0');

// ============================================================================
// SUBCOMPONENTS
// ============================================================================

/**
 * Single stage card in the funnel strip.
 *
 * note (optional): secondary helper line rendered below the main label in a
 * more muted style. Used on conditional stages (4 and 5) to communicate that
 * these are subset populations, not mandatory sequential steps.
 *
 * dimConnector: renders the outgoing arrow in a softer shade to visually
 * signal that the next stage is a conditional population.
 */
const FunnelStage = ({
  stageNum,
  label,
  count,
  color,
  icon: Icon,
  isLast = false,
  note = null,
  dimConnector = false,
}) => (
  <div className="flex items-center gap-1 flex-1 min-w-0">
    <div className={`
      flex-1 min-w-0 rounded-2xl border p-4 flex flex-col gap-2
      bg-slate-900/60 backdrop-blur-sm
      border-${color}-500/25
    `}>
      {/* Stage number + icon */}
      <div className="flex items-center justify-between">
        <span className={`text-xs font-medium text-${color}-400/70 uppercase tracking-wider`}>
          Stage {stageNum}
        </span>
        <div className={`w-7 h-7 rounded-lg bg-${color}-500/15 flex items-center justify-center`}>
          <Icon className={`w-4 h-4 text-${color}-400`} />
        </div>
      </div>
      {/* Count */}
      <div className={`text-3xl font-bold text-${color}-300 leading-none`}>
        {fmtN(count)}
      </div>
      {/* Primary label */}
      <div className="text-xs text-slate-400 leading-tight">{label}</div>
      {/* Conditional note — muted helper for subset stages */}
      {note && (
        <div className="text-xs text-slate-600 leading-tight">{note}</div>
      )}
    </div>
    {/* Arrow connector — softened when next stage is conditional */}
    {!isLast && (
      <div className="flex-shrink-0 px-0.5">
        <ArrowRight className={`w-4 h-4 ${dimConnector ? 'text-slate-700' : 'text-slate-600'}`} />
      </div>
    )}
  </div>
);

/** Ratio KPI card */
const RatioCard = ({ label, value, numeratorLabel, denominatorLabel, color, description }) => (
  <div className={`
    rounded-2xl border p-5 bg-slate-900/60 backdrop-blur-sm
    border-${color}-500/25
  `}>
    <div className="flex items-start justify-between mb-3">
      <span className="text-xs font-medium text-slate-400 uppercase tracking-wider leading-tight max-w-[70%]">
        {label}
      </span>
      <div className={`text-2xl font-bold text-${color}-300 leading-none`}>
        {fmtPct(value)}
      </div>
    </div>
    <div className="text-xs text-slate-500 leading-relaxed">{description}</div>
    {(numeratorLabel || denominatorLabel) && (
      <div className={`mt-2 text-xs text-${color}-400/60`}>
        {numeratorLabel} ÷ {denominatorLabel}
      </div>
    )}
  </div>
);

/** Expandable definition row */
const DefinitionRow = ({ term, definition }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-800/60 last:border-0">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between py-3 px-4 text-left hover:bg-slate-800/30 transition-colors rounded-xl"
      >
        <span className="text-sm font-medium text-slate-300">{term}</span>
        {open
          ? <ChevronUp className="w-4 h-4 text-slate-500 flex-shrink-0" />
          : <ChevronDown className="w-4 h-4 text-slate-500 flex-shrink-0" />
        }
      </button>
      {open && (
        <div className="px-4 pb-3 text-sm text-slate-400 leading-relaxed">
          {definition}
        </div>
      )}
    </div>
  );
};

/** Period selector button */
const PeriodBtn = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`
      px-4 py-1.5 rounded-xl text-sm font-medium transition-all
      ${active
        ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-900/30'
        : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-700/60'
      }
    `}
  >
    {label}
  </button>
);

// ============================================================================
// MAIN VIEW
// ============================================================================

export default function FlowQualityFunnelView() {
  const { user } = useContext(AuthContext);
  const tenantId = user?.tenantId ?? null;

  const [period,      setPeriod]      = useState(30);
  const [rollups,     setRollups]     = useState([]);
  const [recovered,   setRecovered]   = useState(0);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async () => {
    if (!tenantId) {
      setError('No tenant context. Cannot load analytics.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const dateIds = buildDateRange(period);
      // Fetch rollups + conversion metrics in parallel.
      // getConversionMetrics is all-time (not period-scoped) — noted in definitions.
      const [docs, conversion] = await Promise.all([
        getDailyRollups(tenantId, dateIds),
        getConversionMetrics(tenantId, {}).catch(() => null),
      ]);
      setRollups(docs);
      // notYetConvertedEngineCount = referrals that re-ran the Gate and passed.
      // Same number as Business Metrics "Converted" card — all-time, not period-scoped.
      setRecovered(
        conversion?.notYetResolution?.notYetConvertedEngineCount
        ?? conversion?.convertedCases
        ?? 0
      );
      setLastRefresh(new Date());
    } catch (err) {
      console.error('[FlowQualityFunnelView] load error:', err);
      setError(safeUserError(err) ?? 'Failed to load analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [tenantId, period]);

  useEffect(() => { load(); }, [load]);

  // Compute totals once per rollups/period change
  const totals = useMemo(() => getFlowQualityTotals(rollups), [rollups]);

  // ── Ratios ────────────────────────────────────────────────────────────────
  //
  // First-Pass Acceptance Rate:
  //   (first outcome ROUTINE + first outcome URGENT) / Gate Submissions
  //
  // Routine Clean Rate:
  //   first outcome ROUTINE / Gate Submissions
  //   (not ÷ Referrals Written — both counters measure the same committed set)
  //
  // Closed-Loop Rate:
  //   Reached Closed Loop / Entered Ops
  //   (end-to-end pipeline yield from ops entry to terminal completion)
  const firstPassRate    = pct(totals.firstPassAccepted, totals.gateSubmissions);
  const routineCleanRate = pct(totals.routineClean,      totals.gateSubmissions);
  const closedLoopRate   = pct(totals.reachedClosedLoop, totals.enteredOps);

  // ── Render: loading ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center animate-pulse">
            <Activity className="w-5 h-5 text-indigo-400" />
          </div>
          <div>
            <div className="h-5 w-48 bg-slate-700/60 rounded animate-pulse mb-1" />
            <div className="h-3 w-32 bg-slate-800/60 rounded animate-pulse" />
          </div>
        </div>
        <div className="grid grid-cols-6 gap-2 mb-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-2xl bg-slate-800/40 h-36 animate-pulse" />
          ))}
        </div>
        <div className="grid grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="rounded-2xl bg-slate-800/40 h-28 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  // ── Render: error ─────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="rounded-2xl border border-red-500/25 bg-red-950/20 p-8 text-center">
          <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-red-300 mb-2">Failed to Load Analytics</h3>
          <p className="text-red-400/80 text-sm mb-6">{error}</p>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-red-800/50 hover:bg-red-700/50 text-red-200 text-sm font-medium transition-all"
          >
            <RotateCcw className="w-4 h-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  const hasData = totals.gateSubmissions > 0 || totals.referralsWritten > 0;

  // ── Render: main ──────────────────────────────────────────────────────────
  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-indigo-400" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-white">Flow Quality Funnel</h1>
            <p className="text-sm text-slate-400">
              Flow quality from gate submission to closed-loop completion
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Period selector */}
          <div className="flex items-center gap-1 p-1 rounded-xl bg-slate-800/60">
            <PeriodBtn label="7d"  active={period === 7}  onClick={() => setPeriod(7)} />
            <PeriodBtn label="30d" active={period === 30} onClick={() => setPeriod(30)} />
            <PeriodBtn label="90d" active={period === 90} onClick={() => setPeriod(90)} />
          </div>
          {/* Refresh */}
          <button
            onClick={load}
            title={lastRefresh ? `Last refreshed ${lastRefresh.toLocaleTimeString()}` : 'Refresh'}
            className="w-9 h-9 rounded-xl bg-slate-800/60 hover:bg-slate-700/60 flex items-center justify-center text-slate-400 hover:text-white transition-all"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ── Empty state ── */}
      {!hasData && (
        <div className="rounded-2xl border border-slate-700/40 bg-slate-900/40 p-10 text-center">
          <Activity className="w-10 h-10 text-slate-600 mx-auto mb-4" />
          <h3 className="text-base font-medium text-slate-400 mb-1">No data for this period</h3>
          <p className="text-sm text-slate-500">
            No referral activity was recorded in the last {period} days.
          </p>
        </div>
      )}

      {/* ── Six-stage funnel strip ── */}
      {hasData && (
        <div className="rounded-2xl border border-slate-700/30 bg-slate-900/30 p-5">
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-4">
            Pipeline Stages — Last {period} days
          </div>
          <div className="flex items-stretch gap-1">

            {/* Stage 1 — Gate Attempts */}
            <FunnelStage
              stageNum={1}
              label="Gate Attempts"
              count={totals.gateSubmissions}
              color="blue"
              icon={Shield}
            />

            {/* Stage 2 — Referrals Written */}
            <FunnelStage
              stageNum={2}
              label="Referrals Written"
              count={totals.referralsWritten}
              color="teal"
              icon={FileText}
            />

            {/* Stage 3 — Entered Ops (dimConnector: next stage is conditional) */}
            <FunnelStage
              stageNum={3}
              label="Entered Ops"
              count={totals.enteredOps}
              color="indigo"
              icon={ClipboardList}
              dimConnector
            />

            {/* Stage 4 — Recovered from Not Yet (conditional subset) */}
            <FunnelStage
              stageNum={4}
              label="Recovered from Not Yet"
              count={recovered}
              color="amber"
              icon={RefreshCw}
              note="conditional · incomplete referrals recovered"
              dimConnector
            />

            {/* Stage 5 — Triage Exceptions (conditional subset) */}
            <FunnelStage
              stageNum={5}
              label="Triage Exceptions"
              count={totals.triageExceptions}
              color="orange"
              icon={AlertTriangle}
              note="conditional · exception interventions only"
            />

            {/* Stage 6 — Reached Closed Loop */}
            <FunnelStage
              stageNum={6}
              label="Reached Closed Loop"
              count={totals.reachedClosedLoop}
              color="emerald"
              icon={CheckCircle2}
              isLast
            />

          </div>
        </div>
      )}

      {/* ── Ratio KPIs ── */}
      {hasData && (
        <div>
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">
            Quality Ratios
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <RatioCard
              label="First-Pass Acceptance Rate"
              value={firstPassRate}
              numeratorLabel="Routine + Urgent"
              denominatorLabel="Gate Submissions"
              color="teal"
              description="Referrals that passed the Gate on first attempt without any missing-field rejection — a proxy for referring provider Pack compliance."
            />
            <RatioCard
              label="Routine Clean Rate"
              value={routineCleanRate}
              numeratorLabel="Routine"
              denominatorLabel="Gate Submissions"
              color="emerald"
              description="First outcome ROUTINE as a share of all gate submissions. No red flags, all required fields present — the cleanest, lowest-friction outcome."
            />
            <RatioCard
              label="Closed-Loop Rate"
              value={closedLoopRate}
              numeratorLabel="Reached Closed Loop"
              denominatorLabel="Entered Ops"
              color="violet"
              description="Referrals that entered the ops pipeline and reached a closed-loop outcome — end-to-end pipeline yield from first intake action to terminal completion."
            />
          </div>
        </div>
      )}

      {/* ── Metric Definitions ── */}
      <div className="rounded-2xl border border-slate-700/30 bg-slate-900/30 overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-800/60">
          <Info className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-400">Metric Definitions</span>
        </div>
        <div className="px-2 py-1">
          <DefinitionRow
            term="Gate Submissions"
            definition="Successful non-redirect committed submissions — referrals that reached a ROUTINE, NOT_YET, or URGENT gate outcome and completed the atomic write. REDIRECT outcomes are excluded; they do not create a referral document. In normal operation Gate Submissions and Referrals Written are equal because the atomic batch writes both counters together."
          />
          <DefinitionRow
            term="Referrals Written"
            definition="Unique referral core documents created in Firestore. Written atomically on the same commit as the gate submission. Should match Gate Submissions under normal operation; any divergence indicates a partial write failure or duplicate-block event."
          />
          <DefinitionRow
            term="Entered Ops"
            definition="Referrals for which an intake coordinator has taken the first action — either claiming ownership or applying a status transition. Written once per referral (idempotent). Represents referrals that have moved beyond submission into active ops management."
          />
          <DefinitionRow
            term="Recovered from Not Yet"
            definition="Conditional subset. Referrals that initially received a NOT_YET outcome (missing required fields) and were subsequently re-submitted through the Gate engine to obtain a ROUTINE or URGENT outcome. Not every referral passes through this stage — only those that were incomplete at first submission. All-time count sourced from the same engine as Business Metrics 'Converted'; does not reflect the selected period filter."
          />
          <DefinitionRow
            term="Triage Exceptions"
            definition="Conditional subset. Referrals where a triage nurse applied the first clinical exception after the Gate decision: an override of the engine outcome, an on-hold flag, or a priority escalation above NORMAL. Written once per referral (idempotent). Not every referral passes through triage intervention — this measures how often clinical judgment departs from the original Gate decision."
          />
          <DefinitionRow
            term="Reached Closed Loop"
            definition="Referrals whose workflow advanced all the way to CLOSED_LOOP status — the specialist confirmed the patient was seen and the referral was sealed. This is the terminal form state. Referrals can reach Closed Loop without passing through Stages 4 or 5."
          />
          <DefinitionRow
            term="First-Pass Acceptance Rate"
            definition="(First outcome ROUTINE + first outcome URGENT) ÷ Gate Submissions. The share of referrals that passed the Gate without any missing-field rejection on first submission. Higher is better — reflects referring provider adherence to Pack criteria."
          />
          <DefinitionRow
            term="Routine Clean Rate"
            definition="First outcome ROUTINE ÷ Gate Submissions. The share of all submissions that were completely clean — no red flags, all required fields present — on first attempt. A tighter quality measure than First-Pass Acceptance Rate because it excludes URGENT outcomes."
          />
          <DefinitionRow
            term="Closed-Loop Rate"
            definition="Reached Closed Loop ÷ Entered Ops. The share of referrals that entered the ops pipeline and made it all the way to a closed-loop outcome. Measures end-to-end pipeline yield from first intake action to terminal completion."
          />
        </div>
      </div>

      {/* ── Footer note ── */}
      <p className="text-xs text-slate-600 text-center pb-2">
        All data sourced from daily rollup aggregates. Zero PHI reads. Zero collection scans.
        {lastRefresh && <> Last updated {lastRefresh.toLocaleTimeString()}.</>}
      </p>

    </div>
  );
}
